import { HTTP_STATUS_CODE } from '@citylytics/http/constants';
import { AUTH_SKIP_PATHS, HTTP_STATUS_CODE_KEY, SKIP_AUTH_RETRY, } from './core-api-client.constants.js';
export function shouldSkipUnauthorizedRetry(url) {
    return AUTH_SKIP_PATHS.some((path) => url.includes(path));
}
export function hasSkipAuthRetryFlag(request) {
    return SKIP_AUTH_RETRY in request && request[SKIP_AUTH_RETRY] === true;
}
/**
 * The SDK passes `meta` through to the interceptor at runtime, but the
 * interceptor's options type does not declare it, so it is read structurally.
 */
export function hasSkipAuthRetryMeta(options) {
    if (!('meta' in options)) {
        return false;
    }
    const { meta } = options;
    return (typeof meta === 'object' &&
        meta !== null &&
        'skipAuthRetry' in meta &&
        meta.skipAuthRetry === true);
}
/**
 * Records the status alongside the thrown body. Errors without a response are
 * transport failures and are left untouched, as is anything already an Error,
 * since spreading one would drop its message.
 */
export function withHttpStatusCode(params) {
    const { error, response } = params;
    if (!response || error === null || typeof error !== 'object' || error instanceof Error) {
        return error;
    }
    return { ...error, [HTTP_STATUS_CODE_KEY]: response.status };
}
function readHttpStatusCode(error) {
    if (error === null || typeof error !== 'object' || !(HTTP_STATUS_CODE_KEY in error)) {
        return null;
    }
    const statusCode = error[HTTP_STATUS_CODE_KEY];
    return typeof statusCode === 'number' ? statusCode : null;
}
/**
 * A municipality that has never been imported answers 404, as does one whose
 * fence was deleted. Neither is a failure worth an error box, so both become the
 * empty state and the caller only has to handle "nothing here".
 */
export async function fetchOrNull(fetch) {
    try {
        return await fetch();
    }
    catch (caughtError) {
        if (readHttpStatusCode(caughtError) === HTTP_STATUS_CODE.NOT_FOUND) {
            return null;
        }
        throw caughtError;
    }
}
/**
 * The items of a list fetched as one full page (`size: LIST_PAGE_MAX_SIZE`), for screens that
 * need the whole list at once (pickers, client-side search). Past one page it throws rather
 * than quietly leaving items out: that is the point to page through the API instead.
 */
export function readWholeList(params) {
    const { items, hasMore, listName } = params;
    if (hasMore) {
        throw new Error(`The ${listName} list is longer than one page; page through it instead`);
    }
    return items ?? [];
}
